// "History — Published Schema": lists the pages/posts this tool has added schema to on the
// connected site, found via the durable `_audit_schema` meta the Approve button writes. Shown at
// the bottom of both the Schema tab and the Express Schema tab, using the same Content Drawer
// styling in both places.
//
// A filter toggle separates the two ways schema gets onto a page:
//   • Added to an already-live page via the Schema tab or Express Schema tool  ← what users track.
//   • Baked into a page the tool CREATED (new pages, service-area pages) at creation time — those
//     carry a `_lsg_tool_created` / `_lsg_service_area` timestamp. Hidden by default.
// A tool-created page whose schema was EDITED LATER (via the Schema/Express tools) is treated as
// user-added and stays visible: its schema timestamp lands well after its creation timestamp. At
// creation the two are only seconds/a-minute apart (the create call, then the schema write), so a
// gap past EDIT_AFTER_CREATION_MS means a genuine later edit.
import { useState, useCallback, useEffect } from "react";
import { getActiveSiteKey } from "../lib/siteData";

// Margin that comfortably clears the create→schema-write pipeline (incl. slow AI schema generation)
// but is far shorter than any realistic "user edits schema later" gap.
const EDIT_AFTER_CREATION_MS = 5 * 60 * 1000;

// In-memory cache of the scanned history, keyed by active site, so switching tabs (which unmounts
// this component) doesn't re-scan the whole site every time — the scan (getAllPagesAndPosts) is
// expensive. Seeded on mount for instant display; the Refresh button (and the first scan of a
// session) always re-fetch and update this. Cleared on a full page reload, so the data is scanned
// fresh at most once per session per site. Switching sites keys to a different entry.

interface Props {
  connected: boolean;
  getClient?: () => import("../lib/wpClient").WPClient | null;
}

interface Row { id: number; title: string; url: string; type: "page" | "post"; types: string[]; date: string; bakedAtCreation: boolean }

const historyCache = new Map<string, Row[]>();

// True when this page's schema was baked in at creation and never edited afterward — i.e. the tool
// CREATED the page (createdTs present) and the schema timestamp isn't meaningfully later. A later
// edit (schemaTs >> createdTs) makes this false, so the row counts as user-added schema.
function isBakedAtCreation(createdTs: string, schemaTs: string): boolean {
  if (!createdTs) return false; // pre-existing page — schema was always added by the user
  const created = Date.parse(createdTs);
  const stamped = Date.parse(schemaTs);
  if (isNaN(created)) return false;
  if (isNaN(stamped)) return true; // tool-created, no schema-edit stamp → treat as creation-baked
  return stamped - created <= EDIT_AFTER_CREATION_MS;
}

// Pull the distinct @type values out of a page's stored _audit_schema JSON-LD blocks.
function schemaTypesOf(blocks: string[]): string[] {
  const out = new Set<string>();
  for (const s of blocks) {
    try {
      const o = JSON.parse(s) as { "@type"?: unknown };
      const t = o["@type"];
      if (Array.isArray(t)) t.forEach(x => out.add(String(x)));
      else if (t) out.add(String(t));
    } catch { /* skip unparseable block */ }
  }
  return [...out];
}

// ISO timestamp → a short, readable date. Empty/unparseable → "—".
function fmtDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function SchemaHistory({ connected, getClient }: Props) {
  // Seed from the session cache so a tab switch shows the previously-scanned rows instantly instead
  // of re-scanning and flashing "Scanning…".
  const [rows, setRows] = useState<Row[]>(() => historyCache.get(getActiveSiteKey()) ?? []);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(() => historyCache.has(getActiveSiteKey()));
  // Default view = only schema the user added to existing pages (hide schema that shipped with
  // tool-created pages), which is what this history is for.
  const [hideCreated, setHideCreated] = useState(true);

  const load = useCallback(async () => {
    const client = getClient?.();
    if (!connected || !client) return;
    setLoading(true);
    try {
      const pages = await client.getAllPagesAndPosts();
      const withSchema = pages
        .filter(p => Array.isArray(p.auditSchema) && p.auditSchema.length > 0)
        // Date = the tool's durable "schema added" timestamp when present (pages touched from this
        // build on), else the page's last-modified date as a proxy for older pages.
        .map(p => ({ id: p.id, title: p.title, url: p.url, type: p.type, types: schemaTypesOf(p.auditSchema), date: p.schemaTs || p.modified, bakedAtCreation: isBakedAtCreation(p.createdTs, p.schemaTs) }))
        // Latest schema first — the most recently added/updated page sits at the top.
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      historyCache.set(getActiveSiteKey(), withSchema); // retain across tab switches this session
      setRows(withSchema);
      setLoaded(true);
    } catch { /* keep previous rows */ }
    finally { setLoading(false); }
  }, [connected, getClient]);

  // Scan once per session per site: on mount only fetch if this site hasn't been cached yet. A tab
  // switch remounts with the cache already seeded, so it shows instantly with no re-scan. Use the
  // Refresh button to pull the latest (e.g. after approving new schema).
  useEffect(() => { if (connected && !historyCache.has(getActiveSiteKey())) void load(); }, [connected, load]);

  const createdCount = rows.filter(r => r.bakedAtCreation).length;
  const visible = hideCreated ? rows.filter(r => !r.bakedAtCreation) : rows;

  const emptyMsg = !connected
    ? "Connect your WordPress site to see which pages have had schema added by the tool."
    : !loaded ? "Scanning the site…"
    : rows.length === 0 ? "No pages have had schema added by the tool on this site yet."
    // Connected + loaded, but the filter hid everything.
    : `All ${createdCount} page${createdCount === 1 ? "" : "s"} with schema were created by the tool. Uncheck the filter to see them.`;
  const refreshBtn = (
    <button className="cd-refresh-btn" onClick={() => void load()} disabled={loading || !connected}>{loading ? "Scanning…" : "↻ Refresh"}</button>
  );

  return (
    <div className="cd-section cd-history-section" style={{ marginTop: 24 }}>
      <div className="cd-section-hd">
        <span className="cd-hd-brand">History - Published Schema</span>
        {refreshBtn}
      </div>
      {/* Filter: hide schema that came baked into tool-created (new / service-area) pages, so the
          list shows only schema the user added via the Schema / Express Schema tabs. The info icon
          (a sibling of the label, so it never toggles the box) explains the distinction. Only shown
          once there are rows to filter. */}
      {rows.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "2px 0 14px" }}>
          <button
            type="button"
            role="switch"
            aria-checked={hideCreated}
            onClick={() => setHideCreated(v => !v)}
            title={hideCreated ? "Filter on" : "Filter off"}
            style={{ position: "relative", width: 34, height: 20, borderRadius: 10, border: "none", padding: 0, cursor: "pointer", background: hideCreated ? "#f97316" : "#cbd5e1", transition: "background .15s ease", flex: "0 0 auto" }}
          >
            <span style={{ position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.25)", transform: hideCreated ? "translateX(14px)" : "translateX(0)", transition: "transform .15s ease" }} />
          </button>
          <span className="schema-hist-filter" onClick={() => setHideCreated(v => !v)} style={{ fontSize: 12.5, color: "#475569", cursor: "pointer", userSelect: "none" }}>Only show pages with schema added via the Schema or Express Schema tabs</span>
          <span
            data-tip="When new pages are created — such as service area pages and articles — schema is added by default before they're published, so they'd appear in this list when unfiltered. This filter shows only schema you added via the Schema or Express Schema tabs, for easier viewing."
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 15, height: 15, borderRadius: "50%", border: "1px solid #cbd5e1", color: "#94a3b8", fontSize: 10, fontWeight: 700, fontStyle: "italic", fontFamily: "Georgia, serif", cursor: "help", flex: "0 0 auto" }}
          >i</span>
          {createdCount > 0 && (
            <span style={{ color: "#94a3b8", fontSize: 12.5 }}>
              ({createdCount} on tool-created page{createdCount === 1 ? "" : "s"} {hideCreated ? "hidden" : "shown"})
            </span>
          )}
        </div>
      )}
      {visible.length === 0 ? (
        <p className="cd-help">{emptyMsg}</p>
      ) : (
        <table className="cd-history">
          <thead><tr><th>Page</th><th>URL</th><th>Schema Added</th><th>Date</th></tr></thead>
          <tbody>
            {visible.map(p => (
              <tr key={`${p.type}-${p.id}`}>
                <td>{p.title || "(untitled)"}</td>
                <td><a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", wordBreak: "break-all" }}>{p.url}</a></td>
                <td>{p.types.length ? p.types.join(", ") : "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(p.date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
